import { Button, Col, Drawer, Form, Input, Row, Select } from 'antd';
import { Formik } from 'formik';
import { FiCheck } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import type { UserDrawerProps, UserType } from '../../Users.types.ts';
import {
  USERS_FORM,
  USERS_FORM_SCHEMA,
  USERS_INITIAL_FORM_VALUES,
} from '../../Users.constants.tsx';

import './UserDrawer.styles.css';
import { useTranslation } from 'react-i18next';
import { getUserValues } from '../../Users.helpers.ts';
import { useService } from '../../Users.hooks.ts';
import { searchLeadership } from '../../../organization-unit-lists/OrganizationUnitLists.services.ts';
import { DebounceSelect } from '../../../../components/debonce-select';
import { checkUser } from '../../Users.services.ts';

export default function UserDrawer({ onClose, open, onSubmit, userId }: UserDrawerProps) {
  const { t } = useTranslation();
  const { users } = useService();

  const data = users?.find((user: UserType) => user.id === userId);

  const initialValues = userId ? getUserValues(data) : USERS_INITIAL_FORM_VALUES;

  return (
    <Drawer
      title={data?.displayName || t('pages.users.create')}
      placement="right"
      closable={false}
      width={500}
      onClose={onClose}
      open={open}
      extra={
        <Row justify="end" align="middle" gutter={8}>
          <span onClick={onClose} style={{ cursor: 'pointer' }}>
            <MdClose fontSize={20} />
          </span>
        </Row>
      }
      footer={
        <Row justify="end" gutter={8}>
          <Col>
            <Button size="large" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </Col>
          <Col>
            <Button
              size="large"
              type="primary"
              icon={<FiCheck />}
              iconPosition="end"
              htmlType="submit"
              form={USERS_FORM}
            >
              {t('common.confirm')}
            </Button>
          </Col>
        </Row>
      }
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
        validationSchema={USERS_FORM_SCHEMA}
      >
        {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => {
          return (
            <Form layout="vertical" onFinish={handleSubmit} id={USERS_FORM}>
              <Form.Item
                label={t('form.field.name')}
                validateStatus={touched.user && errors.user ? 'error' : ''}
                help={touched.user && typeof errors.user === 'string' ? errors.user : undefined}
              >
                <DebounceSelect
                  mode="multiple"
                  variant="underlined"
                  value={values.user}
                  placeholder={t('form.placeholder.select-here')}
                  fetchOptions={(term) => searchLeadership(term)}
                  style={{ width: '100%' }}
                  onChange={async (newValue) => {
                    if (Array.isArray(newValue)) {
                      setFieldValue('user', newValue);
                      setFieldValue('email', newValue[0].key);
                      setFieldValue('entraId', newValue[0].value);
                      const res = await checkUser(newValue[0]?.value);

                      if (res?.user) {
                        setFieldValue('id', res?.user.id);
                        setFieldValue(
                          'roles',
                          res?.user.roles.map((role: { type: number }) => role.type),
                        );
                      }
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.email')}
                validateStatus={touched.email && errors.email ? 'error' : ''}
                help={touched.email && typeof errors.email === 'string' ? errors.email : undefined}
              >
                <Input
                  name="order"
                  variant="underlined"
                  value={values.email}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                  disabled
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.roles')}
                validateStatus={touched.roles && errors.roles ? 'error' : ''}
              >
                <Select
                  mode="tags"
                  variant="underlined"
                  value={values.roles}
                  placeholder={t('form.placeholder.select-here')}
                  onChange={(e) => setFieldValue('roles', e)}
                  options={[
                    { value: 1, label: t('form.option.specification-approver') },
                    { value: 2, label: t('form.option.company-agreement-approver') },
                    // { value: 3, label: t('form.option.implementation-approver') },
                    { value: 4, label: t('form.option.administrator') },
                    { value: 5, label: t('form.option.auditor') },
                  ]}
                />
              </Form.Item>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
}
